import I18n from 'I18n';
import { getOrderedByFieldTimestamp, getActiveSLAStatus, getHasBeenRepliedTo, getViewMemberProps, isInboxThreadListMember } from 'find-and-filter-data/view-members-schema/protected';
import { getViewMemberId } from 'find-and-filter-data/view-members-schema/public';
import { getFullName, getKnownVisitorDisplayName, useVisitor } from 'find-and-filter-data/visitors/public';
import { useCallback, memo, useMemo } from 'react';
import { buildAriaLabel } from 'svh-workspace-ui-lib/preview-card/public/operators';
import PreviewCardChannelIcon from './PreviewCardChannelIcon';
import PreviewCardContent from './PreviewCardContent';
import PreviewCardHeader from './PreviewCardHeader';
import { PreviewCardWrapper, PreviewCardLeftSidebar, PreviewCardBody } from './PreviewCardStyles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getHeaderText = ({
  isThreadListMember,
  nameText,
  subject
}) => {
  if (isThreadListMember) {
    return {
      primaryHeader: nameText,
      secondaryHeader: subject
    };
  }
  return {
    primaryHeader: subject || I18n.text('conversations-inbox-ui.preview-card.noTicketName'),
    secondaryHeader: nameText
  };
};
const PreviewCard = /*#__PURE__*/memo(({
  hasSLAAlertsAccess = false,
  bottomLeftIcon,
  disabled,
  isSelected,
  isUnread,
  member,
  handleClick,
  publishFailed,
  viewOrderBy,
  renderAbsoluteTimestampOverride = false,
  disableBulkSelect,
  disableBulkSelectTooltip = false,
  hideBulkSelectCheckbox = false,
  handleBatchUpdatesChange,
  isBulkSelected,
  showInboxSLATour,
  shouldDisableSLA,
  onInboxSLATourOpen,
  onInboxSLATourClose,
  unknownContactText = I18n.text('conversations-inbox-ui.unknownVisitor')
}) => {
  //This function gets required props for HDVMS and IVMS
  //to keep component lean
  const {
    associatedContact,
    vid,
    source,
    preview,
    subject,
    noSourceIcon,
    isComment,
    isLatestMessageADraft,
    attachmentTypes,
    isForwarded,
    latestMessageTimestamp,
    latestReceivedTimestamp,
    latestSentTimestamp
  } = getViewMemberProps(member);
  const SlaBadge = hasSLAAlertsAccess === true ? getActiveSLAStatus(member) : null;

  //Parse associated contact full name from thread list member record if it exists.
  const associatedContactFullName = associatedContact && getFullName(associatedContact);

  //Fetch the full visitor object if the associated contact does not exist
  //or if it doesn't contain a full name.
  const {
    data: visitor
  } = useVisitor({
    skip: !!associatedContactFullName,
    vid: vid
  });
  const nameText = useMemo(() => visitor && getKnownVisitorDisplayName(visitor) || unknownContactText, [visitor, unknownContactText]);
  const isUnknownContact = useMemo(() => {
    return !associatedContact && nameText === I18n.text('conversations-inbox-ui.preview-card.unknown-contact');
  }, [associatedContact, nameText]);
  const ariaLabel = buildAriaLabel(source, subject, preview, visitor || undefined);
  const hasNoConversation = preview === I18n.text('conversations-inbox-ui.preview-card.conversation-not-started');

  //Set up handle keypress handler.
  const handleKeyPress = useCallback(event => {
    if (event.key === 'Enter') handleClick();
  }, [handleClick]);
  const isThreadListMember = isInboxThreadListMember(member);
  const {
    primaryHeader,
    secondaryHeader
  } = getHeaderText({
    isThreadListMember,
    nameText: associatedContactFullName || nameText,
    subject
  });
  return /*#__PURE__*/_jsxs(PreviewCardWrapper, {
    onClick: handleClick,
    disabled: disabled,
    onKeyPress: handleKeyPress,
    isSelected: isSelected,
    isUnread: isUnread,
    "data-test-id": "thread-list-member-row",
    "data-selected": isSelected,
    "data-unread": isUnread,
    "data-selenium-thread-latest": latestMessageTimestamp,
    "data-selenium-thread-last-recieved": latestReceivedTimestamp,
    "data-selenium-thread-last-sent": latestSentTimestamp,
    hideBulkSelectCheckbox: hideBulkSelectCheckbox,
    "aria-label": ariaLabel,
    role: "row",
    tabIndex: 0,
    children: [/*#__PURE__*/_jsxs(PreviewCardLeftSidebar, {
      children: [/*#__PURE__*/_jsx(PreviewCardChannelIcon, {
        disableBulkSelect: disableBulkSelect,
        disableBulkSelectTooltip: disableBulkSelectTooltip,
        hideBulkSelectCheckbox: hideBulkSelectCheckbox,
        disabled: disabled,
        onSelect: handleBatchUpdatesChange,
        isBulkSelected: isBulkSelected,
        fallbackIcon: noSourceIcon,
        isSelected: isSelected,
        channelId: source,
        isUnread: isUnread
      }), bottomLeftIcon]
    }), /*#__PURE__*/_jsxs(PreviewCardBody, {
      "data-test-id": "member-body",
      children: [/*#__PURE__*/_jsx(PreviewCardHeader, {
        isUnread: isUnread,
        isUnknownContact: isUnknownContact,
        text: primaryHeader,
        isNameHeader: isThreadListMember,
        isLatestMessageADraft: isLatestMessageADraft,
        renderAbsoluteTimestampOverride: renderAbsoluteTimestampOverride,
        timestamp: getOrderedByFieldTimestamp(viewOrderBy, member) || getOrderedByFieldTimestamp('LATEST_TICKET', member)
      }), /*#__PURE__*/_jsx(PreviewCardContent, {
        showInboxSLATour: showInboxSLATour,
        onInboxSLATourOpen: onInboxSLATourOpen,
        onInboxSLATourClose: onInboxSLATourClose,
        secondaryHeader: secondaryHeader,
        isNameHeader: !isThreadListMember,
        isUnread: isUnread,
        isUnknownContact: isUnknownContact,
        hasNoConversation: hasNoConversation,
        publishFailed: publishFailed,
        isComment: isComment,
        textPreview: preview !== null && preview !== void 0 ? preview : null,
        slaStatus: SlaBadge,
        repliedTo: getHasBeenRepliedTo(member),
        forwarded: isForwarded,
        attachmentTypes: attachmentTypes,
        shouldDisableSLA: shouldDisableSLA,
        isLatestMessageADraft: isLatestMessageADraft
      })]
    })]
  }, getViewMemberId(member));
});
PreviewCard.displayName = 'PreviewCard';
export default PreviewCard;